import { Modal, ModalHeader, ModalBody, ModalFooter, Label, Input } from "reactstrap";
import { IMembershipTitlesByCategoryNameResponse, IPositionTitleInfo } from "../../../../types/position";
import { AddIcon, CancelIcon, CheckIcon } from "../../../common/icons";
import { useState } from "react";
import SearchIndividual from "../../../position/position-detail/assign-individual/search-individual";
import CreateIndividualDetail from "../../../position/position-detail/assign-individual/create-individual-details";
import { IIndividualLight, IIndividualInput, IAddMembershipInput, IAssignMembershipResultResponse, ISearchIndividualsForNewInput, PositionMembershipTypes } from "../../../../types/individual";
import useToggle, { Toggleable } from "../../../../hook/useToggle";
import { CREATE_INDIVIDUAL, IS_INDIVIDUAL_NAME_EXISTS, IS_EMAIL_EXISTS, ASSIGN_MEMBERSHIP, SEARCH__POSITION_INDIVIDUALS } from "../../../../graphql/individual-gql";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { notifyError, notifySuccess } from "../../../../services/notification";
import Select from "../../../common/select/select";
import { GET_ORG_ROLES, MEMBERSHIP_TITLES_BY_CATEGORYNAME } from "../../../../graphql/position-gql";
import confirm from "../../../common/confirm";
import SpinnerOverlay from "../../../common/spinner/spinner";
import { IOrg } from "../../../../types/org";
import { CategoryType } from "../../../../types/constants";
import IndividualsList from "./individuals-list";
import { messages } from "../../../../i18n/message";
import { LOCALES } from "../../../../i18n/locales";
import Button from "../../../common/button";

interface AddMembershipProp extends Toggleable {
    org: IOrg;
}

const AddMembershipModal = (prop: AddMembershipProp) => {
    const { org, isOpen, toggle} = prop;
    
    const { data: membershipTitlesResponse } = useQuery<IMembershipTitlesByCategoryNameResponse>(MEMBERSHIP_TITLES_BY_CATEGORYNAME, {
        variables: { categoryName: 'FORMLGROUP' }, fetchPolicy: "network-only"
    });   
    const [createIndividual] = useMutation(CREATE_INDIVIDUAL);
    const [assignMembership, { loading: assignLoading }] = useMutation<IAssignMembershipResultResponse>(ASSIGN_MEMBERSHIP);
    const [searchIndividual, { loading: searchLoading, error: searchError }] = useLazyQuery(SEARCH__POSITION_INDIVIDUALS)
    const [checkEmailExists] = useLazyQuery(IS_EMAIL_EXISTS);
    const [checkIndividualNameExists] = useLazyQuery(IS_INDIVIDUAL_NAME_EXISTS)
    
    const [selectedBusinessEntityIds, setSelectedBusinessEntityIds] =  useState<number[]>([])
    const [showCreateTable, setShowCreateTable] = useState<boolean>(false)

    // table search results
    // individuals contains all the results from the api
    // filteredIndividuals contains individuals that are OPTIONALLY filtered based on the value of membershipType
    const [individuals, setIndividuals] =  useState<Partial<IIndividualLight>[]>([]);
    const [filteredIndividuals, setFilteredIndividuals] =  useState<Partial<IIndividualLight>[]>([]);
    // top portion of the form
    const [membershipTitleId, setMembershipTitleId] = useState<number>(0);
    const [membershipType, setMembershipType] = useState<PositionMembershipTypes>(PositionMembershipTypes.PositionIndividual);

    const [isContinue, setIsContinue] = useState(false);
    const { isOpen : isCreateIndividualOpen, toggle : createToggle } = useToggle()
    const onToggle = () => {
        isCreateIndividualOpen && createToggle()
        // clear/reset
        setMembershipTitleId(0);
        setMembershipType(PositionMembershipTypes.PositionIndividual);
        setShowCreateTable(false);
        setSelectedBusinessEntityIds([]);
        setIndividuals([]);
        setFilteredIndividuals([]);
        // hide the modal
        toggle();
    }

    const onSearchIndividualHandle = (searchIndividualsInput:ISearchIndividualsForNewInput) => {
        setShowCreateTable(false);
        triggerSearch(searchIndividualsInput);
    }

    function filterIndividuals(individualsParam: Partial<IIndividualLight>[] | undefined, 
        membershipTypeParam: PositionMembershipTypes | undefined): Partial<IIndividualLight>[] {
        
        const toFilter = individualsParam 
            ? individualsParam
            : individuals;

        const membershipFilter = membershipTypeParam
            ? membershipTypeParam
            : membershipType;

        return membershipFilter === PositionMembershipTypes.PositionIndividual
            ? toFilter.filter((item) => item.individualId !== null)
            : toFilter.filter((item) => item.positionId !== null);
    }

    const triggerSearch = (searchIndividualsInput: ISearchIndividualsForNewInput) => {
        searchIndividual({
            variables: { input: searchIndividualsInput },
            fetchPolicy: "network-only" // skip the apollo cache, always get results from the server
        }).then(response => {
            const searchResults = response.data?.searchPositionIndividuals as Partial<IIndividualLight>[];
            
            setIndividuals(searchResults);
            
            // we're setting the filtered items here because if we put the condition on the data passed
            // to the child component, state changes that cause repaints will re-evaluate the condition
            // resetting the page navigation
            // example: if there are 3 pages of data, navigate to page 3, check an item
            // if the membership type is PositionIndividual, the repaint causes the navigation to reset because of the filter
            // if the membership type is PositionPosition, the repaint does not cause the navigation to reset, since there's no filter
            setFilteredIndividuals(filterIndividuals(searchResults, membershipType));
            
            setSelectedBusinessEntityIds([]);            
        }).catch((reason) => {
            console.error(reason);
            setIndividuals([]);
            setFilteredIndividuals([]);
            setSelectedBusinessEntityIds([]);
        });

        if (isCreateIndividualOpen) {
            setShowCreateTable(false);
            createToggle();
        }
    }

    const onOpenIndividualDetailHandle = () => {
        setIndividuals([]);
        setFilteredIndividuals([]);
        setSelectedBusinessEntityIds([]);
        setShowCreateTable(true);
        createToggle();
    }

    const onCreateIndividualHandle =(individualInput:IIndividualInput) => {
        if(isContinue){
            triggerCreateIndividual(individualInput)
        }else{
            triggerSearch({individualName:`${individualInput.firstName} ${individualInput.lastName}`,orgId:0})
            Promise.all([
                checkEmailExists({variables:{email:individualInput.email},fetchPolicy: "network-only"}),
                checkIndividualNameExists({variables:{firstName:individualInput.firstName,lastName:individualInput.lastName},fetchPolicy: "network-only"})
            ]).then(results => {
                if(results[0].data?.isIndividualEmailExists){
                    notifyError('Cannot create a new individual. Email address is already in use.')
                }else if(results[1].data?.isIndividualNameExists){
                    setIsContinue(true);
                    notifyError('There are already Individuals in the system with the same first and last name. If you want to use an existing individual, please select them from the list and click "Assign". If you would still like to create a new individual, click on the "Create" button.')
                }else{
                    triggerCreateIndividual(individualInput)
                }
            })
            .catch(error => {
                console.error('An error occurred:', error);
                notifyError('An error occurred while checking individual name and email.');
            });
        }
    }

    const triggerCreateIndividual = (individualInput: IIndividualInput) => {
        createIndividual({
            variables:{input:individualInput},

            onCompleted: (data) => {
                const newIndi : Partial<IIndividualLight> =  {
                        individualId: data.createIndividual.individualId,
                        individualFullName: data.createIndividual.individualFullName,
                        email: data.createIndividual.email
                    }
                const newIndividuals: Partial<IIndividualLight>[] = individuals
                    ? [newIndi,...individuals ]
                    : [newIndi];

                setSelectedBusinessEntityIds([newIndi.individualId!])
                setIndividuals(newIndividuals)
                setFilteredIndividuals(newIndividuals);
                notifySuccess()
            },
            onError: (error) => {
              console.error("Mutation error:", error.message);
              notifyError('An error occurred while creating new individual.')
            },
        })
        setIsContinue(false)
        createToggle()
    }

    const addMemberConfirm = () => {
        return confirm({
          title: '',
          content: messages[LOCALES.ENGLISH].INFO['5.1'],
          okText: <><CheckIcon /> OK</>,
          okBtnColor: 'primary',
          titleCenter: true,
          bodyCenter: true
        })
      }

    const onAddHandle = () => {
        addMemberConfirm().then(()=> addMemberExecution());
    }

    const addMemberExecution = async () => {
        try {
            // consolidate the data from the different state objects
            // verify the selected membership id's are what is currently visible in the filtered list
            // in case there's a bug with the state management or anything
            const allowed = membershipType === PositionMembershipTypes.PositionIndividual
                ? filteredIndividuals.map((item) => item.individualId!)
                : filteredIndividuals.map((item) => item.positionId!);

            // sanitize data before sending to the api
            // - filter the list to what should be allowed based on the membership type
            // - remove any duplicate ids
            const uniqueMemberIds = Array.from(new Set(selectedBusinessEntityIds.filter((businessEntityId) => allowed.includes(businessEntityId))));

            const addMembershipInput: Partial<IAddMembershipInput> = {
                orgId: org.id,
                titleId: membershipTitleId,
                type: membershipType,
                memberIds: uniqueMemberIds,
                isInformalGrp: org.details?.category?.type === CategoryType.INFORMALGROUP
            }

            const { data } = await assignMembership({
                variables: { memberIds: addMembershipInput.memberIds, input: addMembershipInput },
                refetchQueries: getRefetchQuery(),
                awaitRefetchQueries: true,
            });

            if (data?.assignMembership?.success ) {
                data && notifySuccess();
            } else if (data?.assignMembership?.message) {
                notifyError(data.assignMembership.message);
            }

            onToggle();
        } catch (error:any) {
          notifyError('An error occurred while assigning an individual.');
        }
    }

    const getRefetchQuery = () => [{ query: GET_ORG_ROLES, variables: { orgId: org.id }}];
    const isAddButtonDisabled = (): boolean => membershipTitleId === 0 || selectedBusinessEntityIds.length === 0;

    const handleMembershipTitleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setMembershipTitleId(parseInt(event.target.value));
    }

    const handleMembershipTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const updatedMembershipType = PositionMembershipTypes.PositionIndividual === event.target.value 
            ? PositionMembershipTypes.PositionIndividual
            : PositionMembershipTypes.PositionPosition;
        
        setMembershipType(updatedMembershipType);

        // if we're toggling the membership type, we also need to toggle the dataset passed to the individuals list table
        setFilteredIndividuals(filterIndividuals(individuals, updatedMembershipType));

        if (isCreateIndividualOpen && updatedMembershipType === PositionMembershipTypes.PositionPosition) {
            setShowCreateTable(false);
            createToggle();
        }
    }

    const onSelectHandle = (selectedBusinessEntityId: number, isSelected: boolean): void => {
        const updatedSelectedItems = isSelected
            ? [...selectedBusinessEntityIds, selectedBusinessEntityId]
            : selectedBusinessEntityIds.filter(n => n !== selectedBusinessEntityId);
        
        setSelectedBusinessEntityIds(updatedSelectedItems);
    }

    return(<>
        {assignLoading && <SpinnerOverlay />}
        <Modal size="xl" isOpen={isOpen} toggle={onToggle} scrollable backdrop="static" autoFocus={false}>
            <ModalHeader toggle={onToggle} tag={'h2'}>Add Individual to Membership</ModalHeader>
            <ModalBody>
                <p className="mb-0">Please select a title and type of membership to create</p>
                <div className="row">
                    <div className="col-4"><Label for="title">Membership Title: (required)</Label></div>
                    <div className="col-8">
                        <Select id="title" value={membershipTitleId} onChange={handleMembershipTitleChange} autoFocus={true}>
                            <option value="0">Select a membership title</option>
                            {
                                membershipTitlesResponse?.membershipTitlesByCategoryName
                                    .filter((item: IPositionTitleInfo) => item.status)
                                    .map((item: IPositionTitleInfo) => (
                                        <option key={item.positionTitleId} value={item.positionTitleId}>
                                            {item.positionName}
                                        </option>
                                    ))
                            }
                        </Select>
                    </div>
                </div>
                <div className="row" >
                    <div className="col-4">Membership Type: (required)</div>
                    <div className="col-4">
                        <Label>
                            <Input type="radio"
                                name="type" 
                                id="indBased"
                                value={PositionMembershipTypes.PositionIndividual}
                                defaultChecked={membershipType === PositionMembershipTypes.PositionIndividual}
                                className="right-5px"
                                onChange={handleMembershipTypeChange}
                            />
                            Individual-based</Label>
                    </div>
                    <div className="col-4">
                        <Label>
                            <Input type="radio"
                                name="type"
                                value={PositionMembershipTypes.PositionPosition}
                                defaultChecked={membershipType === PositionMembershipTypes.PositionPosition}
                                className="right-5px"
                                onChange={handleMembershipTypeChange}
                            />
                            Position-based
                        </Label>
                    </div>
                </div>
                <hr style={{color: "black",backgroundColor: "black"}} className="mb-0 mt-0"/>
                <div className="wrapper tooltipOMSContainer">                    
                    <SearchIndividual 
                        onSearchIndividual={onSearchIndividualHandle} 
                        onCreateNewIndividual={onOpenIndividualDetailHandle} 
                        showPosition={true} 
                        disableCreate={membershipType === PositionMembershipTypes.PositionPosition || showCreateTable} 
                        isRole={true} />

                    {isCreateIndividualOpen && <CreateIndividualDetail 
                        toggle={createToggle} 
                        onCreate={onCreateIndividualHandle} 
                        createButtonChange={setIsContinue} />}

                    <IndividualsList
                        onSelect={onSelectHandle}
                        individuals={filteredIndividuals} 
                        selectedBusinessEntityIds={selectedBusinessEntityIds} 
                        loading={searchLoading} 
                        membershipType={membershipType}
                        showCreateTable={showCreateTable} />
                </div>
            </ModalBody>
            <ModalFooter>
                <Button color="primary" onClick={onAddHandle} disabled={isAddButtonDisabled() }>
                    <AddIcon/> Add
                </Button>
                <Button color="secondary" onClick={onToggle}>
                    <CancelIcon/> Cancel
                </Button>
            </ModalFooter>
        </Modal>
    </>
    )
}

export default AddMembershipModal;
